import React, { useEffect } from 'react';
import { handlePrint } from '../utils/printUtils';
import './CwtsForm.css';

function CwtsForm({ studentData }) {
  // Add debugging to see what data is coming in
  useEffect(() => {
    console.log('CwtsForm received studentData:', studentData);
  }, [studentData]);

  // Check if studentData is null, undefined, or empty
  if (!studentData) {
    console.error('CwtsForm: studentData is missing');
    return <div className="cwts-form-error">No student data available for CWTS form.</div>;
  }
  
  // Helper function to safely get values with fallbacks
  const getValue = (key, fallback = '') => {
    try {
      return studentData[key] || fallback;
    } catch (e) {
      console.error(`Error accessing ${key}:`, e);
      return fallback;
    }
  };
  
  // Format birthdate if it exists
  const formatBirthdate = (birthdate) => {
    if (!birthdate) return '';
    
    try {
      const date = new Date(birthdate);
      if (isNaN(date.getTime())) return birthdate;
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) {
      console.error('Error formatting birthdate:', e);
      return birthdate;
    }
  };
  
  // Local print handler that ensures this specific form is printed
  const printThisForm = () => {
    console.log('Print button clicked in CwtsForm component');
    
    try {
      // Get this specific form element
      const form = document.querySelector('.cwts-form');
      if (form) {
        console.log('Form found, preparing for print');
        // Add a data attribute to help identify this form for printing
        form.setAttribute('data-print-target', 'true');
      } else {
        console.error('Could not find form element to print');
      }
      
      // Call the handlePrint utility
      handlePrint();
      
      // Clean up after printing
      setTimeout(() => {
        if (form) {
          form.removeAttribute('data-print-target');
        }
      }, 2000);
    } catch (error) {
      console.error('Error in printThisForm:', error);
      // Fallback to default print
      window.print();
    }
  };

  return (
    <div className="cwts-form">
      <div className="cwts-form-header">
        <h2>CIVIC WELFARE TRAINING SERVICE (CWTS)</h2>
        <h3>STUDENT INFORMATION DATA SHEET</h3>
      </div>
      
      {/* STUDENT PERSONAL INFORMATION */}
      <table className="form-table">
        <thead>
          <tr>
            <th colSpan="4" className="section-header">STUDENT PERSONAL INFORMATION</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <div className="form-label">Last Name</div>
              <div className="form-value">{getValue('lastName')}</div>
            </td>
            <td>
              <div className="form-label">First Name</div>
              <div className="form-value">{getValue('firstName')}</div>
            </td>
            <td>
              <div className="form-label">Middle Name</div>
              <div className="form-value">{getValue('middleName')}</div>
            </td>
            <td>
              <div className="form-label">Student ID</div>
              <div className="form-value">{getValue('studentId')}</div>
            </td>
          </tr>
          <tr>
            <td>
              <div className="form-label">Birthdate</div>
              <div className="form-value">{formatBirthdate(getValue('birthdate'))}</div>
            </td>
            <td>
              <div className="form-label">Sex</div>
              <div className="form-value">{getValue('sex') || getValue('gender')}</div>
            </td>
            <td>
              <div className="form-label">Contact Number</div>
              <div className="form-value">{getValue('contactNumber')}</div>
            </td>
            <td>
              <div className="form-label">Email</div>
              <div className="form-value">{getValue('email')}</div>
            </td>
          </tr>
          <tr>
            <td colSpan="4">
              <div className="form-label">Address</div>
              <div className="form-value">{getValue('address') || ''}</div>
            </td>
          </tr>
        </tbody>
      </table>
      
      {/* EDUCATIONAL INFORMATION */}
      <table className="form-table">
        <thead>
          <tr>
            <th colSpan="4" className="section-header">EDUCATIONAL INFORMATION</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan="2">
              <div className="form-label">Course</div>
              <div className="form-value">{getValue('course')}</div>
            </td>
            <td colSpan="2">
              <div className="form-label">Year Level</div>
              <div className="form-value">{getValue('yearLevel')}</div>
            </td>
          </tr>
          <tr>
            <td colSpan="4">
              <div className="form-label">College/Department</div>
              <div className="form-value">{getValue('college') || getValue('department')}</div>
            </td>
          </tr>
        </tbody>
      </table>
      
      {/* EMERGENCY CONTACT */}
      <table className="form-table">
        <thead>
          <tr>
            <th colSpan="4" className="section-header">EMERGENCY CONTACT</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan="2">
              <div className="form-label">Name</div>
              <div className="form-value">{getValue('emergencyContactName')}</div>
            </td>
            <td colSpan="2">
              <div className="form-label">Relationship</div>
              <div className="form-value">{getValue('emergencyContactRelationship')}</div>
            </td>
          </tr>
          <tr>
            <td colSpan="2">
              <div className="form-label">Contact Number</div>
              <div className="form-value">{getValue('emergencyContactNumber')}</div>
            </td>
            <td colSpan="2">
              <div className="form-label">Address</div>
              <div className="form-value">{getValue('emergencyContactAddress')}</div>
            </td>
          </tr>
        </tbody>
      </table>
      
      {/* CWTS SPECIFIC INFORMATION */}
      <table className="form-table">
        <thead>
          <tr>
            <th colSpan="4" className="section-header">CWTS SPECIFIC INFORMATION</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan="2">
              <div className="form-label">CWTS Component</div>
              <div className="form-value">CWTS</div>
            </td>
            <td colSpan="2">
              <div className="form-label">Registration Date</div>
              <div className="form-value">{formatBirthdate(getValue('registrationDate'))}</div>
            </td>
          </tr>
          <tr>
            <td colSpan="4">
              <div className="form-label">Special Skills/Talents</div>
              <div className="form-value">{getValue('skills') || ''}</div>
            </td>
          </tr>
        </tbody>
      </table>
      
      {/* COMMUNITY SERVICE PREFERENCES */}
      <table className="form-table">
        <thead>
          <tr>
            <th colSpan="4" className="section-header">COMMUNITY SERVICE PREFERENCES</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan="4">
              <div className="form-label">Areas of Interest (Environmental, Health, Education, etc.)</div>
              <div className="form-value">{getValue('interests') || ''}</div>
            </td>
          </tr>
          <tr>
            <td colSpan="4">
              <div className="form-label">Previous Volunteer Experience</div>
              <div className="form-value">{getValue('volunteerExperience') || ''}</div>
            </td>
          </tr>
        </tbody>
      </table>
      
      {/* CERTIFICATION */}
      <div className="certification">
        <p>I hereby certify that the information provided above is true and correct to the best of my knowledge.</p>
        <div className="signature-line">
          <div className="signature-placeholder">Student Signature</div>
          <div className="date-placeholder">Date</div>
        </div>
      </div>
      
      <div className="print-button-container">
        <button className="print-button" onClick={printThisForm}>
          Print Form
        </button>
      </div>
    </div>
  );
}

export default CwtsForm; 